import React from 'react'
import { Card, Table, Button, Space, Tag, Modal, message, Select, Row, Col, Statistic, Switch, Tooltip, InputNumber, Form } from 'antd'
import { DollarOutlined, ReloadOutlined } from '@ant-design/icons'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { api } from '@services/api'
import type { ColumnsType } from 'antd/es/table'
import type { PositionInfo } from '../types/api'

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const formatPrice = (value: unknown): string => {
  const n = toNumber(value, NaN)
  if (!Number.isFinite(n)) return '-'
  const abs = Math.abs(n)
  const decimals = abs >= 1000 ? 2 : abs >= 100 ? 3 : abs >= 1 ? 4 : 6
  return `$${n.toFixed(decimals)}`
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('zh-CN', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const getPositionValueUsdt = (record: PositionInfo): number => {
  const fromApi = toNumber(record.position_value_usdt, NaN)
  return Number.isFinite(fromApi) && fromApi > 0 ? fromApi : 0
}

const getContractsLabel = (record: PositionInfo): string => {
  const exchange = String(record.exchange || '').toLowerCase()
  const contracts = toNumber(record.contracts)
  if (exchange === 'okx') {
    const contractSize = toNumber(record.contract_size, NaN)
    if (Number.isFinite(contractSize) && contractSize > 0) {
      const baseAmount = contracts * contractSize
      return `${contracts} 张 (${baseAmount.toLocaleString('zh-CN', { maximumFractionDigits: 4 })} 标的)`
    }
    return `${contracts} 张`
  }
  return String(contracts)
}

const POSITIONS_REFRESH_MS_KEY = 'trading_positions_refresh_ms'
const POSITIONS_AUTO_REFRESH_KEY = 'trading_positions_auto_refresh'
const DEFAULT_REFRESH_MS = 3000

const REFRESH_INTERVAL_OPTIONS = [
  { label: '2 秒', value: 2000 },
  { label: '3 秒', value: 3000 },
  { label: '5 秒', value: 5000 },
  { label: '10 秒', value: 10000 },
  { label: '30 秒', value: 30000 },
]

const readStoredRefreshMs = (): number => {
  try {
    const raw = localStorage.getItem(POSITIONS_REFRESH_MS_KEY)
    if (raw === null) return DEFAULT_REFRESH_MS
    const n = Number(raw)
    const allowed = REFRESH_INTERVAL_OPTIONS.map((o) => o.value)
    return allowed.includes(n) ? n : DEFAULT_REFRESH_MS
  } catch {
    return DEFAULT_REFRESH_MS
  }
}

const readStoredAutoRefresh = (): boolean => {
  try {
    const raw = localStorage.getItem(POSITIONS_AUTO_REFRESH_KEY)
    if (raw === null) return true
    return raw === '1' || raw.toLowerCase() === 'true'
  } catch {
    return true
  }
}

const getPositionRowKey = (record: PositionInfo): string =>
  [
    record.id,
    record.token_id,
    record.exchange,
    record.symbol,
    record.side,
    String(record.contracts),
    String(record.entry_price ?? ''),
    record.opened_at ?? '',
  ].join('|')

const getLimitCloseStatusMeta = (record: PositionInfo) => {
  const status = String(record.limit_close_order_status || '').toLowerCase()
  if (status === 'filled' || status === 'closed') {
    return { color: 'success', label: '已成交' }
  }
  if (status === 'canceled' || status === 'cancelled' || status === 'expired') {
    return { color: 'default', label: '已撤销' }
  }
  if (record.limit_close_order_id) {
    return { color: 'processing', label: '已挂单' }
  }
  if (record.limit_close_enabled) {
    return { color: 'warning', label: '待保存' }
  }
  return { color: 'default', label: '未设置' }
}

const getStopLossStatusMeta = (record: PositionInfo) => {
  if (record.stop_loss_triggered_at) {
    return { color: 'error', label: '已触发' }
  }
  if (record.stop_loss_enabled) {
    return { color: 'processing', label: '监控中' }
  }
  return { color: 'default', label: '未设置' }
}

const getPositionSideLabel = (side: unknown) => {
  const normalized = String(side || '').toLowerCase()
  if (normalized === 'long' || normalized === 'buy') return { color: 'green', label: '做多' }
  if (normalized === 'short' || normalized === 'sell') return { color: 'red', label: '做空' }
  return { color: 'default', label: normalized || '-' }
}

const Trading: React.FC = () => {
  const queryClient = useQueryClient()
  const [tokenId, setTokenId] = React.useState<number | undefined>(undefined)
  const [autoRefresh, setAutoRefresh] = React.useState(readStoredAutoRefresh)
  const [refreshMs, setRefreshMs] = React.useState(readStoredRefreshMs)
  const [limitCloseDrafts, setLimitCloseDrafts] = React.useState<
    Record<string, { enabled: boolean; price?: number }>
  >({})
  const [savingLimitCloseKey, setSavingLimitCloseKey] = React.useState<string | null>(null)
  const [stopLossDrafts, setStopLossDrafts] = React.useState<
    Record<string, { enabled: boolean; amount?: number }>
  >({})
  const [savingStopLossKey, setSavingStopLossKey] = React.useState<string | null>(null)
  const [closingPosition, setClosingPosition] = React.useState<PositionInfo | null>(null)
  const [closeForm] = Form.useForm<{ closePercentage: number }>()

  const { data: tokens } = useQuery({
    queryKey: ['tokens-for-trading'],
    queryFn: () => api.token.list(),
  })

  // 获取持仓列表（定时刷新标记价与盈亏）
  const {
    data: positions,
    isLoading: positionsLoading,
    isFetching: positionsFetching,
    dataUpdatedAt,
    refetch: refetchPositions,
  } = useQuery({
    queryKey: ['positions', tokenId],
    queryFn: () => api.trading.getPositions(tokenId ? { token_id: tokenId } : undefined),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: autoRefresh ? refreshMs : false,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
  })

  const handleAutoRefreshChange = (checked: boolean) => {
    setAutoRefresh(checked)
    try {
      localStorage.setItem(POSITIONS_AUTO_REFRESH_KEY, checked ? '1' : '0')
    } catch {
      /* ignore */
    }
    if (checked) void refetchPositions()
  }

  const handleRefreshMsChange = (ms: number) => {
    setRefreshMs(ms)
    try {
      localStorage.setItem(POSITIONS_REFRESH_MS_KEY, String(ms))
    } catch {
      /* ignore */
    }
    if (autoRefresh) void refetchPositions()
  }

  const positionSummary = React.useMemo(() => {
    const rows = positions || []
    const totalPositionValue = rows.reduce((sum, row) => sum + getPositionValueUsdt(row), 0)
    const totalUnrealizedPnl = rows.reduce((sum, row) => sum + toNumber(row.unrealized_pnl), 0)
    return {
      count: rows.length,
      totalPositionValue,
      totalUnrealizedPnl,
    }
  }, [positions])

  const closePositionMutation = useMutation({
    mutationFn: ({ positionId, closePercentage }: { positionId: number; closePercentage: number }) =>
      api.trading.closePosition(positionId, { type: 'market', close_percentage: closePercentage }),
    onSuccess: async (_data, variables) => {
      const pct = variables.closePercentage
      message.success(pct >= 100 ? '平仓请求已提交' : `已提交 ${pct}% 部分平仓`)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['positions'] }),
        queryClient.invalidateQueries({ queryKey: ['orders'] }),
      ])
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      message.error(detail || error?.message || '平仓失败')
    },
  })

  const getLimitCloseDraft = (record: PositionInfo) => {
    const rowKey = getPositionRowKey(record)
    if (limitCloseDrafts[rowKey]) {
      return { rowKey, draft: limitCloseDrafts[rowKey] }
    }
    const price = toNumber(record.limit_close_price, NaN)
    return {
      rowKey,
      draft: {
        enabled: Boolean(record.limit_close_enabled),
        price: Number.isFinite(price) ? price : undefined,
      },
    }
  }

  const handleSaveLimitClose = async (record: PositionInfo) => {
    const { rowKey, draft } = getLimitCloseDraft(record)
    if (draft.enabled && (!draft.price || draft.price <= 0)) {
      message.error('请填写大于 0 的限价平仓价格')
      return
    }

    setSavingLimitCloseKey(rowKey)
    try {
      await api.trading.updatePositionLimitClose(Number(record.id), {
        enabled: draft.enabled,
        price: draft.enabled ? draft.price : undefined,
        token_id: record.token_id,
        symbol: record.symbol,
        side: record.side,
        exchange: record.exchange,
      })
      message.success(draft.enabled ? '限价平仓单已提交' : '限价平仓已关闭')
      setLimitCloseDrafts((prev) => {
        const next = { ...prev }
        delete next[rowKey]
        return next
      })
      await queryClient.invalidateQueries({ queryKey: ['positions'] })
      await queryClient.invalidateQueries({ queryKey: ['orders'] })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      message.error(detail || error?.message || '保存限价平仓配置失败')
    } finally {
      setSavingLimitCloseKey(null)
    }
  }

  const getStopLossDraft = (record: PositionInfo) => {
    const rowKey = getPositionRowKey(record)
    if (stopLossDrafts[rowKey]) {
      return { rowKey, draft: stopLossDrafts[rowKey] }
    }
    const amount = toNumber(record.stop_loss_amount_usdt, NaN)
    return {
      rowKey,
      draft: {
        enabled: Boolean(record.stop_loss_enabled),
        amount: Number.isFinite(amount) ? amount : undefined,
      },
    }
  }

  const handleSaveStopLoss = async (record: PositionInfo) => {
    const { rowKey, draft } = getStopLossDraft(record)
    if (draft.enabled && (!draft.amount || draft.amount <= 0)) {
      message.error('请填写大于 0 的止损金额（USDT）')
      return
    }

    setSavingStopLossKey(rowKey)
    try {
      await api.trading.updatePositionStopLoss(Number(record.id), {
        enabled: draft.enabled,
        amount_usdt: draft.enabled ? draft.amount : undefined,
        token_id: record.token_id,
        symbol: record.symbol,
        side: record.side,
        exchange: record.exchange,
      })
      message.success(draft.enabled ? '金额止损监控已启用' : '金额止损已关闭')
      setStopLossDrafts((prev) => {
        const next = { ...prev }
        delete next[rowKey]
        return next
      })
      await queryClient.invalidateQueries({ queryKey: ['positions'] })
    } catch (error: any) {
      const detail = error?.response?.data?.detail
      message.error(detail || error?.message || '保存金额止损配置失败')
    } finally {
      setSavingStopLossKey(null)
    }
  }

  const handleClosePosition = (record: PositionInfo) => {
    const positionId = Number(record.id)
    if (!positionId || Number.isNaN(positionId)) {
      message.error('当前持仓缺少可用ID，无法执行平仓')
      return
    }
    closeForm.setFieldsValue({ closePercentage: 100 })
    setClosingPosition(record)
  }

  const handleConfirmClosePosition = async () => {
    if (!closingPosition) return
    const { closePercentage } = await closeForm.validateFields()
    const positionId = Number(closingPosition.id)
    await closePositionMutation.mutateAsync({ positionId, closePercentage })
    setClosingPosition(null)
    closeForm.resetFields()
  }

  // 持仓表格列
  const positionColumns: ColumnsType<PositionInfo> = [
    {
      title: 'Token',
      dataIndex: 'token_id',
      key: 'token_id',
      render: (id) => {
        const token = (tokens || []).find((t: any) => Number(t.id) === Number(id))
        return token ? token.name : '-'
      },
    },
    {
      title: '交易对',
      dataIndex: 'symbol',
      key: 'symbol',
    },
    {
      title: '方向',
      dataIndex: 'side',
      key: 'side',
      render: (side) => {
        const sideMeta = getPositionSideLabel(side)
        return <Tag color={sideMeta.color}>{sideMeta.label}</Tag>
      },
    },
    {
      title: '开仓时间',
      dataIndex: 'opened_at',
      key: 'opened_at',
      width: 180,
      render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'),
    },
    {
      title: '持仓数量',
      dataIndex: 'contracts',
      key: 'contracts',
      align: 'right',
      render: (_, record) => getContractsLabel(record),
    },
    {
      title: '开仓价',
      dataIndex: 'entry_price',
      key: 'entry_price',
      align: 'right',
      render: (price) => {
        return formatPrice(price)
      },
    },
    {
      title: '标记价',
      dataIndex: 'mark_price',
      key: 'mark_price',
      align: 'right',
      render: (price) => {
        return formatPrice(price)
      },
    },
    {
      title: '仓位价值(USDT)',
      dataIndex: 'position_value_usdt',
      key: 'position_value_usdt',
      align: 'right',
      render: (_, record) => {
        const value = getPositionValueUsdt(record)
        return value > 0 ? formatCurrency(value) : '-'
      },
    },
    {
      title: '未实现盈亏',
      dataIndex: 'unrealized_pnl',
      key: 'unrealized_pnl',
      align: 'right',
      render: (pnl) => {
        const numericPnl = toNumber(pnl, NaN)
        if (!Number.isFinite(numericPnl)) return '-'
        const color = numericPnl >= 0 ? '#3f8600' : '#cf1322'
        return <span style={{ color }}>${numericPnl.toFixed(2)}</span>
      },
    },
    {
      title: '保证金率',
      dataIndex: 'margin_ratio',
      key: 'margin_ratio',
      align: 'right',
      render: (ratio) => {
        const numericRatio = toNumber(ratio, NaN)
        return Number.isFinite(numericRatio) ? `${numericRatio.toFixed(2)}` : '-'
      },
    },
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      render: (exchange) => (
        <Tag color="blue" style={{ textTransform: 'uppercase' }}>
          {exchange}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'open' ? 'green' : 'gray'}>
          {status === 'open' ? '持仓中' : '已平仓'}
        </Tag>
      ),
    },
    {
      title: '限价平仓',
      key: 'limit_close',
      width: 280,
      render: (_, record) => {
        const { rowKey, draft } = getLimitCloseDraft(record)
        const statusMeta = getLimitCloseStatusMeta(record)
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space wrap size={4}>
              <Switch
                size="small"
                checked={draft.enabled}
                onChange={(checked) =>
                  setLimitCloseDrafts((prev) => ({
                    ...prev,
                    [rowKey]: { ...draft, enabled: checked },
                  }))
                }
              />
              <InputNumber
                size="small"
                min={0}
                precision={8}
                style={{ width: 120 }}
                disabled={!draft.enabled}
                placeholder="限价"
                value={draft.enabled && Number.isFinite(draft.price) ? draft.price : undefined}
                onChange={(value) =>
                  setLimitCloseDrafts((prev) => ({
                    ...prev,
                    [rowKey]: {
                      ...draft,
                      price: value == null ? undefined : Number(value),
                    },
                  }))
                }
              />
              <Button
                size="small"
                type="primary"
                loading={savingLimitCloseKey === rowKey}
                onClick={() => void handleSaveLimitClose(record)}
              >
                保存
              </Button>
            </Space>
            <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
          </Space>
        )
      },
    },
    {
      title: '金额止损',
      key: 'stop_loss',
      width: 300,
      render: (_, record) => {
        const { rowKey, draft } = getStopLossDraft(record)
        const statusMeta = getStopLossStatusMeta(record)
        const pnl = toNumber(record.unrealized_pnl, NaN)
        const threshold = toNumber(record.stop_loss_amount_usdt, NaN)
        const nearStop =
          draft.enabled &&
          Number.isFinite(pnl) &&
          Number.isFinite(threshold) &&
          threshold > 0 &&
          pnl <= 0 &&
          Math.abs(pnl) >= threshold * 0.8
        return (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space wrap size={4}>
              <Switch
                size="small"
                checked={draft.enabled}
                onChange={(checked) =>
                  setStopLossDrafts((prev) => ({
                    ...prev,
                    [rowKey]: { ...draft, enabled: checked },
                  }))
                }
              />
              <InputNumber
                size="small"
                min={0.01}
                precision={2}
                style={{ width: 120 }}
                disabled={!draft.enabled}
                placeholder="USDT"
                addonAfter="U"
                value={draft.enabled && Number.isFinite(draft.amount) ? draft.amount : undefined}
                onChange={(value) =>
                  setStopLossDrafts((prev) => ({
                    ...prev,
                    [rowKey]: {
                      ...draft,
                      amount: value == null ? undefined : Number(value),
                    },
                  }))
                }
              />
              <Button
                size="small"
                type="primary"
                loading={savingStopLossKey === rowKey}
                onClick={() => void handleSaveStopLoss(record)}
              >
                保存
              </Button>
            </Space>
            <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
            {nearStop && (
              <span style={{ fontSize: 12, color: '#cf1322' }}>
                浮亏 {Math.abs(pnl).toFixed(2)} / 阈值 {threshold.toFixed(2)} USDT
              </span>
            )}
          </Space>
        )
      },
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            type="primary"
            danger
            loading={closePositionMutation.isPending && Number(record.id) === Number((closePositionMutation.variables as any)?.positionId)}
            onClick={() => handleClosePosition(record)}
          >
            平仓
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="page-inner">
      <Card title="持仓管理">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Statistic
              title="总仓位价值"
              value={positionSummary.totalPositionValue}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="USDT"
              valueStyle={{ fontSize: 24, fontWeight: 'bold' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="总未实现盈亏"
              value={positionSummary.totalUnrealizedPnl}
              precision={2}
              prefix={<DollarOutlined />}
              suffix="USDT"
              valueStyle={{
                fontSize: 24,
                fontWeight: 'bold',
                color: positionSummary.totalUnrealizedPnl >= 0 ? '#3f8600' : '#cf1322',
              }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="持仓数量"
              value={positionSummary.count}
              suffix="笔"
              valueStyle={{ fontSize: 24, fontWeight: 'bold' }}
            />
          </Col>
        </Row>
        <Space wrap style={{ marginBottom: 12 }} align="center">
          <Select
            allowClear
            placeholder="按Token筛选"
            style={{ minWidth: 220 }}
            onChange={(v) => setTokenId(v)}
          >
            {(tokens || []).map((t: any) => (
              <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>
            ))}
          </Select>
          <Space size={4} align="center">
            <span style={{ color: '#666' }}>自动刷新</span>
            <Switch checked={autoRefresh} onChange={handleAutoRefreshChange} />
          </Space>
          <Select
            value={refreshMs}
            style={{ width: 110 }}
            disabled={!autoRefresh}
            options={REFRESH_INTERVAL_OPTIONS}
            onChange={handleRefreshMsChange}
          />
          <Tooltip title="立即从交易所拉取最新持仓">
            <Button
              icon={<ReloadOutlined spin={positionsFetching} />}
              loading={positionsFetching && !positions}
              onClick={() => void refetchPositions()}
            >
              刷新
            </Button>
          </Tooltip>
          <span style={{ fontSize: 12, color: '#888' }}>
            {positionsFetching ? '更新中…' : dataUpdatedAt ? `更新于 ${dayjs(dataUpdatedAt).format('HH:mm:ss')}` : ''}
            {autoRefresh ? ` · 每 ${refreshMs / 1000} 秒` : ' · 已暂停自动刷新'}
          </span>
        </Space>
        <Table
          columns={positionColumns}
          dataSource={positions}
          loading={positionsLoading && !positions}
          rowKey={(record) => getPositionRowKey(record)}
          scroll={{ x: 1700 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>
      <Modal
        title={closingPosition ? `确认平仓 ${closingPosition.symbol}` : '确认平仓'}
        open={Boolean(closingPosition)}
        okText="确认平仓"
        cancelText="取消"
        okButtonProps={{ danger: true }}
        confirmLoading={closePositionMutation.isPending}
        onCancel={() => {
          setClosingPosition(null)
          closeForm.resetFields()
        }}
        onOk={() => handleConfirmClosePosition()}
      >
        {closingPosition && (
          <Form form={closeForm} layout="vertical" initialValues={{ closePercentage: 100 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>方向: {closingPosition.side}，数量: {getContractsLabel(closingPosition)}</span>
              <Form.Item label="平仓比例" name="closePercentage" rules={[{ required: true, message: '请设置平仓比例' }]}>
                <InputNumber min={1} max={100} addonAfter="%" style={{ width: 160 }} />
              </Form.Item>
              <Space wrap align="center">
                <span>快捷选择:</span>
                {[25, 50, 75, 100].map((pct) => (
                  <Button
                    key={pct}
                    size="small"
                    onClick={() => closeForm.setFieldsValue({ closePercentage: pct })}
                  >
                    {pct}%
                  </Button>
                ))}
              </Space>
            </Space>
          </Form>
        )}
      </Modal>
    </div>
  )
}

export default Trading

